<template>
  <div class="relative w-full">
    <div
      ref="scrollContainer"
      class="flex gap-10 p-4 overflow-x-auto no-scrollbar md:p-8 select-none"
      :class="config.dragCursor ? 'cursor-grab' : ''"
      @mousedown="startDragging"
      @mousemove="dragging"
      @mouseup="stopDragging"
      @mouseleave="stopDragging"
    >
      <div
        class="flex-none w-64 md:w-96 shadow-lg px-3 pb-8 rounded"
        :class="context.isDarkMode ? 'bg-semidark-silver' : ''"
        v-for="(item, index) of config.items"
        :key="index"
      >
        <div v-if="hasValue(item.tags)" class="flex gap-2 justify-end relative" style="margin-bottom: -5px; margin-top: -15px;">
          <Tag 
            v-for="(tag, index) of item.tags"
            :key="index"
            :value="translationHandler.translate(tag.title, 'ucfirst')"
            :style="{'backgroundColor': tag.bg ?? '#000', 'color': tag.color ?? '#fff'}"
          />
        </div>

        <div v-if="hasValue(item.image)">
          <img 
            :src="item.image" 
            alt="card icon" 
            class="rounded shadow"
          >
        </div>
        <p 
          v-if="hasValue(item.title)" 
          class="text-xl 2xl:text-2xl font-bold mt-10 border-b border-b-2" 
          :class="context.isDarkMode 
            ? `text-third-color border-${getTitleColor(item)}` 
            : `text-void ${getTitleColor(item)}`
          "
          :style="getTitleColor(item)"
        >
          {{ translationHandler.translate(item.title, 'ucfirst') }}
        </p>
        <p class="text-xl font-semibold my-10" :class="context.isDarkMode ? 'text-silver' : 'text-void'">
          {{ translationHandler.translate(item.description, 'ucfirst') }}
        </p>
        <p 
          v-if="hasValue(config.actionButton)" 
          @click="handleButtonClick({args: config.actionButton.args ?? null, item: item})"
          class="mt-10 flex gap-2 content-center primary-button max-width-md rounded-3xl px-4 shadow"
          :class="context.isDarkMode ? 'text-silver' : 'text-silver'"
        >
            <span class="text-lg lg:text-lg 2xl:text-2xl my-auto text-silver font-semibold mx-auto md:mx-0">
                {{ translationHandler.translate(config.actionButton.title ?? 'see page', 'capitalize') }}
            </span>
            <i 
              v-if="hasValue(config.actionButton.icon)" 
              :class="config.actionButton.icon"
              class="text-silver border border-second-color rounded-full bg-second-color p-2 my-auto shadow ml-auto"
            ></i>
        </p>

        <p 
          @click="handleSelection(item)"
          class="mt-5 flex gap-2 content-center secondary-button max-width-md rounded-3xl px-4 shadow"
          :class="context.isDarkMode ? 'text-silver' : 'text-silver'"
        >
            <span class="text-lg lg:text-lg 2xl:text-2xl my-auto text-silver font-semibold mx-auto md:mx-0">
                {{ translationHandler.translate('details', 'capitalize') }}
            </span>
            <i 
              class="pi pi-info text-silver border border-light-mixed rounded-full bg-light-mixed p-2 my-auto shadow ml-auto"
            ></i>
        </p>

      </div>
    </div>
  </div>
</template>

<script setup>
import translationHandler from '@/composables/handlers/TranslationHandler';
import { useContextStore } from '@/stores/context';
import { Tag } from 'primevue';
import { ref, defineProps, defineEmits, onMounted, watch } from 'vue';

const context = useContextStore();
const scrollContainer = ref(null);
let isDragging = false;
let startX = 0;
let scrollLeft = 0;

const props = defineProps({
  dragCursor: {
    type: Boolean,
    required: false,
    default: false
  },
  actionButton: {
    type: Object,
    required: false,
    default: null
  },
  items: {
    required: true,
    default: []
  },
});

const emit = defineEmits(['selected', 'clicked']);

function handleSelection(item = {}) {
  emit('selected', item);
}

function handleButtonClick(values = {}) {
  emit('clicked', values);
}

const config = ref({
    dragCursor: false,
    actionButton: false,
    items: []
});

onMounted(() => {
    config.value.dragCursor = props.dragCursor;
    config.value.items = props.items ?? [];
    config.value.actionButton = props.actionButton ?? null;
});

watch(() => props.dragCursor, (newValue) => {
    config.value.dragCursor = newValue;
});

watch(() => props.items, (newValue) => {
    config.value.items = newValue;
});

function startDragging(e) {
  isDragging = true;
  if (scrollContainer.value) {
    if (config.value.dragCursor) {
        scrollContainer.value.classList.add('active-drag');
    }
    startX = e.pageX - scrollContainer.value.offsetLeft;
    scrollLeft = scrollContainer.value.scrollLeft;
  }
}

function dragging(e) {
  if (!isDragging) return;
  
  e.preventDefault(); 
  
  const x = e.pageX - scrollContainer.value.offsetLeft;
  const walk = (x - startX) * 2;
  scrollContainer.value.scrollLeft = scrollLeft - walk;
}

function stopDragging() {
  isDragging = false;
  if (scrollContainer.value && config.value.dragCursor) {
    scrollContainer.value.classList.remove('active-drag');
  }
}

function hasValue(value) {
  return ![undefined, null, ''].includes(value);
}

function getTitleColor(item) {
  if ([undefined, null, ''].includes(item.titleColor)) {
    return null; 
  }
  return {color: item.titleColor};
}

</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
    display: none;
}
.no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.cursor-grab {
    cursor: grab;
}
.active-drag {
    cursor: grabbing;
}
</style>